import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import NumberOfLicenses from '../shared/NumberOfLicenses';
import ApolloError from '../../../shared/components/ApolloError';
import { useMutation, useReactiveVar } from '@apollo/client';
import PageInstructions from '../../../shared/components/PageInstructions';
import SuccessMessage from '../../../shared/components/SuccessMessage';
import { userPrivileges, userTier } from '../../../../apollo/cache';
import PrivilegeNames from '../../../../constants/privilegeNames';
import FreeLicensesTable from './FreeLicensesTable';
import * as LicenseTypes from '../../constants/LicenseTypes';
import {
  getUserLicenseInformation,
  getLicenseCount
} from '../shared/services/userLicenseInformationService';
import { useUserFreeDataQuery } from '../shared/hooks/useUserFreeDataQuery';
import REMOVE_LICENSE from '../../queries/removeLicenseMutation';
import ConfirmDialog from '@shared/components/confirmDialog/ConfirmDialog';
import GET_FREE_USERS from '../../queries/freeLicensesQueries/getFreeUsers';
import LinkButton from '../../../shared/components/LinkButton';
import { PAGE_MODE } from '../../constants/pageModeTypes';

/**
 * @typedef {object} UserFreeLicensesTableProps
 * @property {string} [licensesModifiedSuccessfullyMessage]
 * @property {number} [orgId]
 * @property {string} [orgName]
 * @property {number} [orgUnitId]
 * @property {number} pageMode
 */

/**
 * @typedef {object} UserLicenseInformation
 * @property {number} [licenseCount]
 * @property {number} [assignedLicenseCount]
 * @property {Array<*>} [users]
 * @property {number} [orgUnitLicenseCount]
 * @property {number} [assignedOrganizationOrgUnitLicenseCount]
 * @property {Array<*>} [organizationOrgUnitUsers]
 */

/**
 * @param {UserFreeLicensesTableProps} props
 */
const UserFreeLicensesTable = props => {
  const {
    licensesModifiedSuccessfullyMessage,
    pageMode,
    orgId = null,
    orgName = '',
    orgUnitId = null
  } = props;

  const currentUserTier = useReactiveVar(userTier);
  const privileges = useReactiveVar(userPrivileges);

  const [userLicenseInformation, setUserLicenseInformation] = useState(
    /** @type {UserLicenseInformation} */ ({})
  );
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectExternalUserIdForDelete, setSelectExternalUserIdForDelete] = useState(null);
  const [licensesModifiedMessage, setLicensesModifiedMessage] = useState(null);

  const getInstructions = () => {
    if (pageMode === PAGE_MODE.MddapOrg) {
      return (
        'The tables below display all free licenses. The first table lists all organization licenses ' +
        'and the second table lists all organizational ' +
        'unit licenses. Both tables can be sorted or filtered depending on the column. ' +
        'Licenses can be assigned or removed after assignment.'
      );
    }

    if (pageMode === PAGE_MODE.OrgUnit) {
      return (
        'The table below displays all free licenses for the organizational unit. ' +
        'Each column in the table can be sorted. Licenses can be assigned or removed after assignment.'
      );
    }

    if (pageMode === PAGE_MODE.FDAVipOrg || pageMode === PAGE_MODE.MDICVipOrg) {
      return (
        'The table below displays all free licenses for the organization. ' +
        'Each column in the table can be sorted. Licenses can be assigned or removed after assignment.'
      );
    }
  };

  const getUsersCallback = ({
    orgUsers,
    orgUnitUsers,
    orgLicenseCount,
    orgAssignedLicenseCount,
    orgUnits,
    orgUnitLicenseCount,
    orgUnitAssignedLicenseCount
  }) => {
    const userLicenseInformation = getUserLicenseInformation(
      orgUsers || orgUnitUsers,
      pageMode === PAGE_MODE.MddapOrg && orgUnitUsers,
      getLicenseCount(orgLicenseCount, orgUnitLicenseCount),
      getLicenseCount(orgAssignedLicenseCount, orgUnitAssignedLicenseCount),
      orgUnits,
      pageMode,
      orgUnitId,
      currentUserTier,
      true
    );
    setUserLicenseInformation(userLicenseInformation);
  };

  const { error } = useUserFreeDataQuery(pageMode, orgId, orgUnitId, getUsersCallback);

  const [removeLicense, { loading: removeLicenseLoading, error: removeLicenseError }] = useMutation(
    REMOVE_LICENSE,
    {
      update: cache => {
        cache.evict({
          query: GET_FREE_USERS
        });
      },
      onCompleted: () => {
        setUserLicenseInformation({});
        setLicensesModifiedMessage('User Successfully Deactivated.');
      }
    }
  );

  const isLoading = _.isEmpty(userLicenseInformation) || removeLicenseLoading;

  useEffect(() => {
    setLicensesModifiedMessage(licensesModifiedSuccessfullyMessage);
  }, [licensesModifiedSuccessfullyMessage]);

  const handleOpenDialog = externalUserId => {
    setSelectExternalUserIdForDelete(externalUserId);
    setOpenConfirmDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectExternalUserIdForDelete(null);
    setOpenConfirmDialog(false);
  };

  const handleRemoveLicense = () => {
    handleCloseDialog();
    removeLicense({
      variables: { externalUserId: selectExternalUserIdForDelete }
    });
  };

  if (error || removeLicenseError) {
    return <ApolloError />;
  }

  return (
    <div className='user-table-of-licenses'>
      <div className='page-header'>
        <PageInstructions
          title='User Management - Free Licenses Instructions'
          description={getInstructions()}
        />
        {privileges.includes(PrivilegeNames.LicenseManagement) && (
          <LinkButton
            data-testid='manage-button'
            className='primary-button sm'
            to='/user-management/license-management'
            state={{
              activeTab: LicenseTypes.FREE.value,
              orgId: orgId,
              orgName: orgName,
              pageMode: pageMode
            }}>
            Manage
          </LinkButton>
        )}
      </div>
      {licensesModifiedMessage && !removeLicenseLoading && (
        <SuccessMessage message={licensesModifiedMessage} />
      )}
      <NumberOfLicenses
        numberOfTotalLicenses={userLicenseInformation.licenseCount}
        numberOfAssignedLicenses={userLicenseInformation.assignedLicenseCount}
        loading={isLoading}
      />
      <FreeLicensesTable
        pageMode={pageMode}
        usersData={userLicenseInformation.users}
        orgId={orgId}
        orgName={orgName}
        loading={isLoading}
        openConfirmDialog={handleOpenDialog}
      />

      {pageMode === PAGE_MODE.MddapOrg && (
        <>
          <NumberOfLicenses
            numberOfTotalLicenses={userLicenseInformation.orgUnitLicenseCount}
            numberOfAssignedLicenses={
              userLicenseInformation.assignedOrganizationOrgUnitLicenseCount
            }
            loading={isLoading}
          />
          <FreeLicensesTable
            usersData={userLicenseInformation.organizationOrgUnitUsers}
            orgId={orgId}
            orgName={orgName}
            pageMode={PAGE_MODE.OrgUnit}
            singleUnit={true}
            loading={isLoading}
            openConfirmDialog={handleOpenDialog}
          />
        </>
      )}

      <ConfirmDialog
        open={openConfirmDialog}
        handleClose={handleCloseDialog}
        handleOnConfirm={handleRemoveLicense}
        message='Are you sure you want to remove this licensed user?'
      />
    </div>
  );
};

export default UserFreeLicensesTable;
